/**
 * Expiry for a dismissed update banner.
 *
 * WHY THIS EXISTS
 *
 * The banner used to store the dismissed build's sha in `sessionStorage` and
 * re-read it on every render, so a dismissal lasted as long as the tab did. In
 * an SPA a tab can live for days — clicking around is `pushState`, not a
 * navigation — so one dismissal could silence every subsequent build for a week
 * (measured 2026-09-20: one user 7 releases and 6.5 days behind).
 *
 * The storage LOCATION is deliberately unchanged: `sessionStorage`, per tab.
 * That is the scope the problem lives in (a long-lived tab), and widening it to
 * `localStorage` would let a dismissal in one tab hide the banner in a tab the
 * user opens fresh afterwards — the opposite of the fix.
 *
 * Pure and DOM-free so the expiry rule is unit-testable on its own.
 */

/**
 * How long a dismissed banner stays hidden before it is shown again. Four hours
 * is about half a working day: long enough that dismissing is a real reprieve
 * and nobody is nagged twice in a morning, short enough that no dismissal can
 * carry a user across a day boundary onto a stale build.
 */
export const DISMISS_RETURN_AFTER_MS = 4 * 60 * 60 * 1000;

/** The shape the PREVIOUS storage format wrote: a bare git sha, or `unknown`. */
const LEGACY_SHA_VALUE = /^(?:[0-9a-f]{7,40}|unknown)$/i;

/** A dismissal of one target build at one instant. */
export interface DismissRecord {
  /** The `latest.sha` that was dismissed. */
  sha: string;
  /**
   * Epoch ms of the dismissal, or null when the stored value predates this
   * format (a bare sha, the shipped v1 shape) and carries no time at all.
   */
  at: number | null;
}

/** The value written to storage. Read back by `parseDismissRecord`. */
export function serializeDismissRecord(sha: string, at: number): string {
  return JSON.stringify({ sha, at });
}

/**
 * Read a stored dismissal. Returns null for anything that is not a usable
 * record — absent, empty, malformed JSON, or the wrong shape.
 *
 * A bare sha string is the PREVIOUS storage format and is still honoured as a
 * record, with `at: null`. It is not silently upgraded to "dismissed now":
 * `isDismissActive` treats an unknown time as expired, so the one banner a user
 * sees immediately after this ships is the correct, conservative direction —
 * toward telling them about a new build, never toward hiding it.
 */
export function parseDismissRecord(raw: string | null | undefined): DismissRecord | null {
  if (typeof raw !== 'string' || raw.length === 0) return null;
  // A legacy value is honoured only if it is shaped like the thing the old code
  // actually wrote — a git sha, or the `unknown` fallback gen-version.mjs bakes
  // when git is unavailable. Mirrors SHA_RE in versionCompare.ts, which is not
  // exported; anything else is corruption and resolves to "show the banner".
  if (raw[0] !== '{') {
    return LEGACY_SHA_VALUE.test(raw) ? { sha: raw, at: null } : null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const candidate = parsed as Record<string, unknown>;
    if (typeof candidate.sha !== 'string' || candidate.sha.length === 0) return null;
    const at = typeof candidate.at === 'number' && Number.isFinite(candidate.at)
      ? candidate.at
      : null;
    return { sha: candidate.sha, at };
  } catch {
    return null;
  }
}

/**
 * Whether the banner should stay hidden for `sha` right now.
 *
 * Every uncertain input resolves to NOT active (i.e. show the banner). That is
 * the safe direction for this control: the cost of showing a banner one extra
 * time is a click, and the cost of hiding one wrongly is a user pinned on an
 * old build, which is the defect being fixed.
 *
 * - no record, or a record for a different build -> show (a new build is a new
 *   decision, which is the pre-existing behaviour and is retained);
 * - no recorded time (legacy bare-sha value) -> show;
 * - a time in the future (a clock change, a tampered value) -> show, rather
 *   than trusting a dismissal that could otherwise never expire;
 * - otherwise hidden until `returnAfterMs` has elapsed.
 */
export function isDismissActive(
  record: DismissRecord | null,
  sha: string,
  now: number,
  returnAfterMs: number = DISMISS_RETURN_AFTER_MS,
): boolean {
  if (!record || record.sha !== sha) return false;
  if (record.at === null) return false;
  if (record.at > now) return false;
  return now - record.at < returnAfterMs;
}
